import { FC, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-regular-svg-icons";

import TableListPreRecorded from "@/shared/components/organisms/TableListPreRecorded";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { toasts } from "@/shared/utils/toasts";

import {
  preRecordedClassesInitialValues,
  preRecordedClassesValidationSchema,
} from "@/shared/schemas/brand.schema";

import {
  usePreRedordedClassesQuery,
  useStorePreRedordedClassesMutation,
  useUpdatePreRedordedClassesMutation,
  useDeletePreRedordedClassesMutation,
} from "@/app/brand/brand.api";
import { useDebounce } from "@/shared/hooks/debounce";

const clases = [];

type RecordedProps = {
  branch_id: number;
  embed: string;
  id: number;
  src: string;
  thumbnail: string;
  title: string;
  youtube_id: string;
};

export const PreRecordedClasses: FC<any> = () => {
  const { id } = useParams();

  const [currentVideoEdit, setCurrentVideoEdit] = useState<RecordedProps>();
  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [searchPreRecordedClasses, setSearchPreRecordedClasses] = useState("");
  const debouncedSearch = useDebounce(searchPreRecordedClasses, 500);

  const [createPreRedordedClasses, CreatePreRedordedClassesResult] =
    useStorePreRedordedClassesMutation();

  const [updatePreRedordedClasses, UpdatePreRedordedClassesResult] =
    useUpdatePreRedordedClassesMutation();

  const [deletePreRedordedClasses, deletePreRedordedClassesResult] =
    useDeletePreRedordedClassesMutation();

  const preRecordedVideoQuery = usePreRedordedClassesQuery({
    branchId: id,
    search: debouncedSearch,
  });

  const functionOpenDialogCreate = () => {
    setOpenDialogCreate(!openDialogCreate);
  };

  const getYouTubeVideoId = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleClose = () => {
    setCurrentVideoEdit(undefined);
  };

  const [videoToDelete, setVideoToDelete] = useState<string | number | null>(
    null
  );
  // Controlar Abre/Cierre del Alert para confirmación de eliminar
  const [openDelete, setOpenDelete] = useState(false);

  const handleOpenDelete = (id: string | number | null) => {
    setVideoToDelete(id);
    setOpenDelete(!openDelete);
  };

  const onDelete = async () => {
    if (videoToDelete !== null) {
      try {
        await deltePreRedordedClasses({
          branchId: id,
          videoId: videoToDelete,
        });
      } catch (error) {
        console.log("Delete");
      }
    }
  };

  const handleCreatePreRedordedClasses = async (values: any) => {
    try {
      await createPreRedordedClasses({
        ...values,
        branchId: id,
      }).unwrap();

      toasts.success(
        "Registro exitoso",
        "La clase se ha registrado correctamente"
      );
      preRecordedVideoQuery.refetch();
      setOpenDialogCreate(!openDialogCreate);
    } catch (error) {
      toasts.danger(
        "Error al registrar",
        "Ha ocurrido un error al registrar la clase"
      );
    }
  };

  const handleUpdatePreRedordedClasses = async (values: any) => {
    try {
      await updatePreRedordedClasses({
        ...values,
        branchId: id,
        videoId: currentVideoEdit.id,
      }).unwrap();
      toasts.success(
        "Video actualizado",
        "Se ha actualizado el video exitosamente"
      );
      preRecordedVideoQuery.refetch();
      setOpenDialogCreate(!openDialogCreate);
    } catch (error) {
      toasts.danger(
        "Error al registrar",
        "Ha ocurrido un error al registrar la clase"
      );
    }
  };

  const handleDeletePreRecordedClasses = async () => {
    try {
      await deletePreRedordedClasses({
        branchId: id,
        videoId: videoToDelete,
      });

      toasts.success(
        "Video eliminado",
        "Se ha eliminado el video exitosamente"
      );
      preRecordedVideoQuery.refetch();
      setOpenDelete(!openDelete);
    } catch (error) {
      toasts.danger(
        "No se eliminó el video",
        "Ha ocurrido un error al eliminar el video"
      );
    }
  };

  return (
    <div className="flex justify-center h-full w-full mt-5">
      <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <TableListPreRecorded
          title={t("additional-videos.views.title")}
          isLoading={
            preRecordedVideoQuery.isLoading || preRecordedVideoQuery.isFetching
          }
          clases={preRecordedVideoQuery.data ?? []}
          search={searchPreRecordedClasses}
          handleChange={(text) =>
            setSearchPreRecordedClasses(text.target.value)
          }
          functionButtonCreated={functionOpenDialogCreate}
          functionButtonEdit={(video) => {
            setCurrentVideoEdit({
              id: video.id,
              title_video: video.title,
              url_video: video.src,
            });
            setOpenDialogCreate(!openDialogCreate);
          }}
          functionButtonDelete={handleOpenDelete}
        />
        <PaginationUI
          currentPage={1}
          totalPages={1}
          onPageChange={() => {}}
          pageSize={15}
          totalResults={
            preRecordedVideoQuery.data ? preRecordedVideoQuery.data.length : 0
          }
        />

        <AlertErrorButtons
          open={openDelete}
          onClose={() => handleOpenDelete(null)}
          title={t("additional-videos.views.delete_video_title")}
          message={t("additional-videos.views.delete_video_description")}
          primaryButtonText={t("general.delete")}
          secondaryButtonText={t("general.cancel")}
          isLoading={deletePreRedordedClassesResult.isLoading}
          onPrimaryButtonClick={handleDeletePreRecordedClasses}
          onSecondaryButtonClick={() => handleOpenDelete(null)}
        />

        <Formik
          enableReinitialize
          initialValues={currentVideoEdit ?? preRecordedClassesInitialValues}
          validationSchema={preRecordedClassesValidationSchema}
          onSubmit={async (values, formikHelpers) => {
            try {
              if (currentVideoEdit) {
                await handleUpdatePreRedordedClasses(values);
              } else {
                await handleCreatePreRedordedClasses(values);
              }
              formikHelpers.resetForm();
            } catch (error) {
              console.error("Error", error);
            }
          }}
        >
          {({
            handleChange,
            values,
            handleSubmit,
            resetForm,
            errors,
            touched,
          }) => {
            const videoId = values.url_video
              ? getYouTubeVideoId(values.url_video as string)
              : null;
            const thumbnailUrl = videoId
              ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
              : "https://placehold.co/800x800"; // Placeholder image

            return (
              <DialogCreate
                open={openDialogCreate}
                setOpen={functionOpenDialogCreate}
                tittle={t("additional-videos.views.new_video")}
                OnclickCreate={handleSubmit}
                OnclickCancel={() => {
                  handleClose();
                  functionOpenDialogCreate();
                  resetForm();
                }}
                tittleButtonCreate={
                  currentVideoEdit ? t("general.edit") : t("general.create")
                }
                tittleButtonCancel={t("general.cancel")}
                onClose={() => {
                  handleClose();
                  resetForm();
                }}
                isLoading={
                  CreatePreRedordedClassesResult.isLoading ||
                  UpdatePreRedordedClassesResult.isLoading
                }
                acceptDisabled={
                  CreatePreRedordedClassesResult.isLoading ||
                  UpdatePreRedordedClassesResult.isLoading
                }
              >
                <form action="">
                  <div className="mt-6 mx-2">
                    <div className="space-y-12">
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t("additional-videos.views.add_video")}
                            </h2>
                          </div>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full flex flex-col items-center justify-center">
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt="Thumbnail del video"
                                className="w-64 h-40"
                              />
                            ) : (
                              <div className="w-64 h-40 flex items-center justify-center border border-gray-300 rounded-lg">
                                <FontAwesomeIcon
                                  icon={faImage}
                                  className="h-10 w-10 text-primary-500"
                                />
                              </div>
                            )}

                            <p className="text-sm text-gray-500 mt-2">
                              {t(
                                "additional-videos.views.thumbnail_disclaimer"
                              )}
                            </p>
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              type="text"
                              name="title_video"
                              value={values.title_video}
                              onChange={handleChange}
                              label={t("additional-videos.views.video_title")}
                              placeholder={t(
                                "additional-videos.views.video_title"
                              )}
                              error={errors.title_video && touched.title_video}
                              textError={errors.title_video}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              type="url"
                              name="url_video"
                              value={values.url_video}
                              onChange={handleChange}
                              label={t("additional-videos.views.url")}
                              placeholder={t("additional-videos.views.url")}
                              error={errors.url_video && touched.url_video}
                              textError={errors.url_video}
                            />
                          </div>
                          <div className="col-span-full flex items-center justify-center">
                            <iframe
                              width="100%"
                              height="215"
                              src={`https://www.youtube.com/embed/${videoId}`}
                              title="YouTube video player"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              referrerPolicy="strict-origin-when-cross-origin"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </DialogCreate>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};
